// La lista para que el asesor mande las difusiones a mano.
//
// Mandar estos avisos por Meta costaba $96 a la semana, y el asesor ya tiene
// una extensión que manda 250 al día. Lo que hacía falta no era mandar: era la
// lista, armada en el momento justo.
//
// Por eso el número grande no es decorativo. Cambia solo entre que se abre la
// pantalla y que se aprieta el botón, y ese cambio es exactamente la gente a
// la que ya no hay que escribirle.

import { useState } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';

import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

import { levanta } from '../dashboard/motion';
import { numero } from '../dashboard/formato';

const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

const cuando = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(String(iso || ''));
  if (!m) return '';
  const f = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  const hoy = new Date();
  const dias = Math.round((new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate())
    - new Date(+m[1], +m[2] - 1, +m[3])) / 86400000);
  const hora = `${m[4]}:${m[5]}`;
  if (dias === 0) return `hoy a las ${hora}`;
  if (dias === 1) return `ayer a las ${hora}`;
  return `el ${DIAS[f.getDay()]} a las ${hora}`;
};

/* Los tres mensajes, con su hora. Viven aquí y no en el servidor porque el
   que los manda es una persona: lo que necesita es poder copiarlos. */
export const MOMENTOS = [
  {
    id: 'viernes',
    dia: 'Viernes',
    hora: '3:00 p.m.',
    para: 'Ya está el menú de la próxima semana',
    texto: 'Hola {nombre}, ya está el menú de la próxima semana en tu app. '
      + 'Entra y elige tus días: te toma dos minutos y así te llega lo que sí te provoca.',
  },
  {
    id: 'sabado',
    dia: 'Sábado',
    hora: '11:00 a.m.',
    para: 'Todavía no ha elegido ningún día',
    texto: 'Hola {nombre}, vimos que aún no eliges tus días de la próxima semana. '
      + 'Entra a tu app y elígelos cuando quieras; tienes hasta mañana domingo a las 10 de la noche.',
  },
  {
    id: 'domingo',
    dia: 'Domingo',
    hora: '10:00 a.m.',
    para: 'Último aviso antes del cierre',
    texto: 'Hola {nombre}, hoy a las 10 de la noche armamos los pedidos de toda la semana. '
      + 'Si no eliges tus días, te armamos el lunes, martes y miércoles automáticamente. '
      + 'Entra a tu app si quieres cambiarlos.',
  },
];

export default function TarjetaDifusion({ estado, onDescargar, onRecargar }) {

  const [bajando, setBajando] = useState('');
  const [repetidos, setRepetidos] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [copiado, setCopiado] = useState('');

  const bajar = (momento) => {
    setBajando(momento);
    setAviso(null);
    onDescargar(momento, repetidos)
      .then((r) => {
        setAviso({
          tipo: r.incluidos === 0 ? 'info' : 'success',
          texto: r.incluidos === 0
            ? 'No hay nadie nuevo: todos los pendientes ya salieron en una descarga de hoy.'
            : `${numero(r.incluidos)} en el archivo`
              + (r.omitidos > 0 ? ` · ${numero(r.omitidos)} ya salieron hoy y no se repiten` : ''),
        });
        onRecargar();
      })
      .catch(() => setAviso({ tipo: 'error', texto: 'No se pudo bajar la lista. Intenta de nuevo.' }))
      .finally(() => setBajando(''));
  };

  const copiar = (m) => {
    navigator.clipboard?.writeText(m.texto).then(() => {
      setCopiado(m.id);
      setTimeout(() => setCopiado(''), 1800);
    }).catch(() => { /* sin portapapeles: el texto está a la vista igual */ });
  };

  const nuevos = estado?.nuevos ?? 0;
  const pasaDelTope = nuevos > 250;

  return (
    <Card sx={{ p: 3, height: 1, display: 'flex', flexDirection: 'column', ...levanta }}>

      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>Envío manual</Typography>
          <Typography variant="h4" sx={{ mt: .25 }}>Lista para difusión</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: .5 }}>
            Semana {estado?.semana || '—'} · no han elegido ningún día
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography variant="h2" sx={{ lineHeight: 1 }}>{numero(nuevos)}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {estado?.yaBajadosHoy > 0
              ? `nuevos · ${numero(estado.yaBajadosHoy)} ya bajados hoy`
              : 'pendientes'}
          </Typography>
        </Box>
      </Stack>

      {/* El tope de la extensión es real: la lista del sábado fueron 282. La
          lista sale ordenada por urgencia, así que los que quedan fuera son
          siempre los que tienen el plan menos ajustado. */}
      {pasaDelTope &&
        <Alert severity="info" sx={{ mt: 2 }}>
          Son más de 250. El archivo viene ordenado por urgencia:
          manda los primeros 250 y el resto quedan para mañana.
        </Alert>
      }

      {aviso && <Alert severity={aviso.tipo} sx={{ mt: 2 }}>{aviso.texto}</Alert>}

      <Divider sx={{ my: 2.5 }} />

      <Stack spacing={1.5}>
        {MOMENTOS.map((m) => (
          <Box key={m.id}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: .75 }}>
              <Chip label={`${m.dia} ${m.hora}`} size="small" variant="outlined" />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{m.para}</Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                fullWidth
                variant="contained"
                color="inherit"
                disabled={!!bajando}
                onClick={() => bajar(m.id)}
                startIcon={bajando === m.id
                  ? <CircularProgress size={16} color="inherit" />
                  : <DownloadRoundedIcon />}
              >
                {bajando === m.id ? 'Armando…' : 'Bajar CSV'}
              </Button>
              <Tooltip title={m.texto} placement="top">
                <Button
                  variant="outlined"
                  color="inherit"
                  sx={{ flexShrink: 0 }}
                  onClick={() => copiar(m)}
                  startIcon={copiado === m.id ? <CheckRoundedIcon /> : <ContentCopyRoundedIcon />}
                >
                  {copiado === m.id ? 'Copiado' : 'Mensaje'}
                </Button>
              </Tooltip>
            </Stack>
          </Box>
        ))}
      </Stack>

      <Divider sx={{ my: 2.5 }} />

      <FormControlLabel
        control={<Checkbox size="small" checked={repetidos}
                           onChange={(e) => setRepetidos(e.target.checked)} />}
        label={
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Incluir a los que ya bajé hoy (solo si el envío se cortó a la mitad)
          </Typography>
        }
      />

      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1 }}>
        {estado?.ultimaDescarga
          ? `Última descarga ${cuando(estado.ultimaDescarga)} · ${numero(estado.ultimoTotal ?? 0)} contactos`
          : 'Todavía no se ha bajado ninguna lista.'}
      </Typography>
    </Card>
  );
}
